import { memo, useId, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { menuState, routeState } from "@/comn/features/recoil";
import { Icon, IconButton } from "@/comn/components";
import { routes } from "@/comn/features/router";
import { useMenu } from "@/comn/features/config/MenuConfig";

const EndFlag = ({ setOverflow }: any) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (!ref.current) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setOverflow(!(entry.intersectionRatio > 0));
            });
        });
        io.observe(ref.current);
        return () => {
            io.disconnect();
        };
    }, []);
    return <li ref={ref} />;
};

const GNBItem = (props: any) => {
    const {
        containerContext,
        index,
        currMid,
        menuId,
        system,
        menuNm,
        bsopClsfCd,
        ptlTpCd,
        children,
        menuUrlAddr = "",
    } = props;

    const id = useId();
    const ref = useRef<any>(null);
    const [hover, setHover] = useState(false);

    useLayoutEffect(() => {
        if (index === null) return;
        if (!ref.current) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                containerContext.current.intersectionLog[index] = entry.intersectionRatio > 0;
            });
        });
        io.observe(ref.current);
        return () => {
            io.disconnect();
        };
    }, []);

    const bsopClsfCdWithFlag = ((bsopClsfCd || "") + (ptlTpCd || "")).toLowerCase();
    const mached = bsopClsfCdWithFlag === system;

    const test123 = (obj: any): any => {
        console.log("menu item:", JSON.stringify(obj, null, 2));
        if (obj.isLeafYn === "Y") {
            return obj.menuUrlAddr;
        } else if (Array.isArray(obj.children) && typeof obj.children[0] === "object") {
            return test123(obj.children[0]);
        } else return;
    };
    //
    // const test123 = (obj: any): any => {
    //     if (obj.isLeafYn === "Y") {
    //         return obj.menuUrlAddr;
    //     } else if (Array.isArray(obj.children)) {
    //         // Find first authorized child instead of blindly taking children[0]
    //         const authorizedChild = obj.children.find((child: any) => child.authYn === "Y");
    //         if (authorizedChild) return test123(authorizedChild);
    //     }
    //     return;
    // };

    let test = test123(props) || menuUrlAddr;

    return (
        <li
            ref={ref}
            className="relative h-12 px-4 flex items-center"
            onMouseEnter={() => {
                setHover(true);
            }}
            onMouseLeave={() => {
                setHover(false);
            }}
        >
            <a
                href={test}
                aria-current={mached ? "page" : "false"}
                className={classNames("group flex items-center gap-2")}
            >
                <Icon
                    icon={bsopClsfCd}
                    className="fill-darkgray group-aria-[current=page]:fill-primary-text dark:fill-text"
                />
                <span className="font-semibold min-w-max text-darkgray text-[15px] dark:text-text group-aria-[current=page]:text-primary-text">
                    {menuNm}
                </span>
            </a>
            {hover &&
                (() => {
                    const { bottom, left, top } = ref.current.getBoundingClientRect();
                    const style = index === null ? { top, left: left + 240 } : { top: bottom, left };

                    return createPortal(
                        <div className="z-[9999] absolute shadow rounded bg-white dark:bg-navigation" style={style}>
                            <div className="text-lg font-semibold p-4">{menuNm}</div>
                            <ul className="flex flex-col w-[360px] gap-1 px-2 pb-2">
                                {children.map((child: any) => {
                                    const { menuId, menuNm, menuUrlAddr = "" } = child;

                                    let test = test123(child) || menuUrlAddr;

                                    return (
                                        <li
                                            className="group"
                                            aria-current={currMid === menuId ? "page" : "false"}
                                            key={`${id}${menuId}`}
                                        >
                                            <a
                                                href={test}
                                                className="block py-2 px-4 break-words rounded hover:bg-gray/10 dark:hover:bg-white/5 group-aria-[current=page]:font-semibold"
                                            >
                                                {menuNm}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>,
                        document.body,
                    );
                })()}
        </li>
    );
};

const GNB = memo(() => {
    const { root, data, system } = useMenu();
    const id = useId();
    const systems: any[] = root?.children || [];
    const _context = useRef({ intersectionLog: {} });
    const [overflow, setOverflow] = useState(false);
    const [overflowed, setOverflowed] = useState<any[]>([]);

    const handleMouseEnter = () => {
        const of = systems.slice(
            Number(
                Object.entries(_context.current.intersectionLog)
                    .filter(([, value]) => {
                        return value;
                    })
                    .sort((a, b) => {
                        return Number(b[0]) - Number(a[0]);
                    })[0][0],
            ),
        );
        setOverflowed(of);
    };

    const handleMouseLeave = (e: any) => {
        setOverflowed([]);
    };

    const currMid = data.menuId;

    return (
        <div className="flex w-full h-full items-center relative">
            <ul className="flex items-center h-full gap-4 overflow-hidden">
                {Boolean(systems.length) &&
                    systems.map((child: any, index: any) => {
                        return (
                            <GNBItem
                                key={`${id}${child.menuId}`}
                                index={index}
                                containerContext={_context}
                                currMid={currMid}
                                system={system}
                                {...child}
                            />
                        );
                    })}
                <EndFlag setOverflow={setOverflow} />
            </ul>

            {overflow && (
                <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <div className="bg-gradient-to-r from-transparent to-45% to-background absolute w-10 h-full right-full z-[1] pointer-events-none" />
                    <IconButton icon="down" />
                    {!!overflowed.length && (
                        <ul className="absolute top-full shadow rounded flex flex-col w-[240px] bg-white z-[9999] dark:bg-navigation">
                            {overflowed.map((item) => (
                                <GNBItem
                                    key={`${id}${item.menuId}`}
                                    index={null}
                                    containerContext={_context}
                                    currMid={currMid}
                                    system={system}
                                    {...item}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
});

const NavItem = (props: any) => {
    const { name, children, base = "", to = "" } = props;
    const depth_1 = (base || to).startsWith("/") ? base || to : "/" + (base || to);

    const setRoute = useSetRecoilState(routeState);

    return (
        <li className="group flex relative items-center px-4">
            <Link to={depth_1}>
                <button className="flex items-center space-x-1">
                    <p>{name}</p>
                    <Icon icon="down" size="xs" className="transition group-hover:rotate-180" />
                </button>
            </Link>
            <div className="pt-2 w-max absolute hidden top-full left-0 group-hover:block">
                <ul className="rounded border shadow bg-dialog p-4 grid grid-cols-2 gap-1 [&>li:hover]:underline">
                    <li className="col-span-2">
                        <Link to={depth_1} className="block p-2 font-semibold">
                            {name}
                        </Link>
                    </li>
                    {children?.map((child: any) => {
                        const { name, base } = child;
                        const depth_2 = base.startsWith("/") ? base : "/" + base;
                        return (
                            <li key={uuid()} onClick={() => setRoute(depth_1 + depth_2)}>
                                <Link to={depth_1 + depth_2} className="block w-40 py-2 px-4">
                                    {name}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </li>
    );
};

export const HeaderMenu = memo(() => {
    const menu = useRecoilValue(menuState);
    const { isDev } = menu;

    return (
        <nav className="flex h-[60px] w-[calc(100%-600px)] items-center">
            {isDev ? (
                <ul className="flex">
                    {routes.map((child: any) => {
                        return <NavItem key={uuid()} {...child} />;
                    })}
                </ul>
            ) : (
                <GNB />
            )}
        </nav>
    );
});
